import axios from "axios";
import { Suspense } from "react";
import * as Sentry from "@sentry/react";
import { TailSpin as Loader } from "react-loader-spinner";
import { ReactQueryDevtools } from "react-query/devtools";
import { QueryClient, QueryClientProvider } from "react-query";
import { BrowserRouter as Router } from "react-router-dom";

import Routes from "./router";
import { AuthProvider } from "./context/Auth";
import { Footer } from "components/Footer/Footer";
import { FeedbackProvider } from "context/Feedback";
import { ControlProvider } from "./context/Controls";
import { ErrorBoundary } from "components/common/ErrorBoundary";
import { TrackingProvider } from "context/Tracking";
import { ConfirmationProvider } from "context/Confirmation";
import { FeatureFlagProvider } from "context/FeatureFlag";

const MAX_RETRIES = 3;
const HTTP_STATUS_TO_NOT_RETRY = [404];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (failureCount > MAX_RETRIES) {
          return false;
        }

        if (
          axios.isAxiosError(error) &&
          HTTP_STATUS_TO_NOT_RETRY.includes(error.response?.status ?? 0)
        ) {
          console.log(`Aborting retry due to ${error.response?.status} status`);
          return false;
        }

        return true;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: 600_000, // 10min
    },
  },
});

Sentry.init({
  dsn: "https://c32f6b2aa099e750728c49d7e7118d4c@o4510787733749760.ingest.de.sentry.io/4510787734732880",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration({
      // NOTE: This will disable built-in masking. Only use this if your site has no sensitive data, or if you've already set up other options for masking or blocking relevant data, such as 'ignore', 'block', 'mask' and 'maskFn'.
      maskAllText: false,
      blockAllMedia: false,
    }),
    Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
  // Enable logs to be sent to Sentry
  enableLogs: true,
});

const Loading = () => {
  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Loader color="#0991b1" height={100} width={100} />
    </div>
  );
};

function App() {
  Sentry.logger.info("User triggered test log", { log_source: "sentry_test" });

  return (
    <Router>
      <ErrorBoundary>
        <ControlProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <FeatureFlagProvider>
                <TrackingProvider>
                  <FeedbackProvider>
                    <Suspense fallback={<Loading />}>
                      <ConfirmationProvider>
                        <Routes></Routes>
                        <Footer />
                      </ConfirmationProvider>
                    </Suspense>

                    <ReactQueryDevtools initialIsOpen={false} />
                  </FeedbackProvider>
                </TrackingProvider>
              </FeatureFlagProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ControlProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
